import { PERMISSIONS, sortPermissions, ungrantable } from "./permissions";
import type { StatusTone } from "./status";
import type { Operator, OperatorStatus, Role, RoleSummary, SignInLinkPurpose } from "./types";

/**
 * Operator accounts: the rules the panel applies before it asks the server.
 *
 * None of this is authority. Every rule here is enforced again by the API, which
 * answers a breach with a 409 or a 400 and a sentence the page shows verbatim.
 * What these functions buy is that an operator is not offered a button the server
 * will always refuse, and that a form says what is wrong while it is being typed
 * rather than after a round trip.
 */

/**
 * Mirrors `MIN_PASSWORD_LENGTH` in `server/src/api/auth/password.ts`. Length only —
 * that file explains why there are no composition rules, and a client that added
 * one would refuse passwords the server accepts.
 */
export const MIN_PASSWORD_LENGTH = 12;

/** A display name, not an identifier. Long enough for any real name. */
export const OPERATOR_NAME_MAX = 120;

/**
 * Same shape as the database's `admin_user_email_shape_check` (`%_@_%._%`): some
 * text, an at-sign, some text, a dot, some text. Deliberately no stricter — a
 * client that refused an address the server would take would be wrong in the
 * direction nobody can work around.
 */
const EMAIL_SHAPE = /^.+@.+\..+$/;

/**
 * What a minted token looks like: 32 random bytes in base64url is 43 characters.
 * The floor is lower than that so a future longer or shorter token does not need a
 * panel release, but high enough that a stray `#section` anchor is not posted to
 * the server as if it were a credential.
 */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{32,}$/;

export function operatorStatusTone(status: OperatorStatus | string): StatusTone {
  if (status === "disabled") return "faulted";
  // Waiting on a person to open a link — neither fine nor broken.
  if (status === "invited") return "transitional";
  if (status === "active") return "available";

  // A status a newer server added. Not an incident; see `customerStatusTone`.
  return "idle";
}

export function linkPurposeLabel(purpose: SignInLinkPurpose): string {
  return purpose === "invite" ? "Invitation" : "Password reset";
}

/**
 * The URL an operator hands to a colleague.
 *
 * **The token goes in the fragment.** A fragment is never sent in a request, so it
 * cannot appear in the reverse proxy's access log, in this server's request log, or
 * in a `Referer` header if the page ever links out. A query string would do all
 * three, and would still work — which is why this is tested rather than trusted.
 */
export function signInLinkUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}/set-password#${token}`;
}

/** The token out of `location.hash`, or `undefined` for anything that is not one. */
export function readLinkToken(hash: string): string | undefined {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  return TOKEN_SHAPE.test(raw) ? raw : undefined;
}

/** Whether a set of roles includes the built-in Administrator. */
export function holdsAdministrator(roles: readonly RoleSummary[]): boolean {
  return roles.some((role) => role.grantsAll);
}

/**
 * Whether the actor may hand this role to someone: they must hold every
 * permission it carries (the server's no-escalation rule). Administrator carries
 * the whole catalog, so only an actor holding all of it may assign it.
 */
export function canAssignRole(held: readonly string[], role: Role): boolean {
  const carries = role.grantsAll ? PERMISSIONS : role.permissions;
  return ungrantable(held, carries).length === 0;
}

/**
 * What an operator would be able to do with these roles and direct grants — the
 * same union the server computes, for previewing a change before saving it.
 */
export function effectivePermissions(
  roles: readonly Role[],
  direct: readonly string[],
): readonly string[] {
  if (roles.some((role) => role.grantsAll)) return sortPermissions(PERMISSIONS);
  return sortPermissions([...roles.flatMap((role) => role.permissions), ...direct]);
}

/** Adds or removes one role id, returning a new list. */
export function toggleRole(roleIds: readonly string[], id: string): readonly string[] {
  return roleIds.includes(id) ? roleIds.filter((candidate) => candidate !== id) : [...roleIds, id];
}

export interface InviteForm {
  readonly email: string;
  readonly name: string;
  readonly roleIds: readonly string[];
  readonly permissions: readonly string[];
}

export interface InviteFormIssues {
  readonly email?: string;
  readonly name?: string;
  readonly access?: string;
}

/**
 * What is wrong with an invitation as typed so far.
 *
 * An empty email or name reports nothing: a form that opens already covered in
 * errors teaches an operator to ignore them. The submit button stays disabled
 * until both are present, which is the signal for "not finished yet".
 */
export function inviteFormIssues(form: InviteForm): InviteFormIssues {
  const email = form.email.trim();
  const name = form.name.trim();

  return {
    ...(email !== "" && !EMAIL_SHAPE.test(email)
      ? { email: "Enter a full address, like name@example.com." }
      : {}),
    ...(form.name !== "" && name === ""
      ? { name: "A name is needed so the audit log can say who did what." }
      : {}),
    ...(name.length > OPERATOR_NAME_MAX
      ? { name: `At most ${OPERATOR_NAME_MAX} characters.` }
      : {}),
    ...(form.roleIds.length === 0 && form.permissions.length === 0
      ? { access: "Choose a role or a permission — an account with neither could see nothing." }
      : {}),
  };
}

export interface PasswordIssues {
  readonly password?: string;
  readonly confirm?: string;
}

/**
 * A new password and its confirmation. The confirmation is only judged once
 * something has been typed into it — before that, "does not match" is true and
 * useless.
 */
export function passwordIssues(password: string, confirm: string): PasswordIssues {
  return {
    ...(password !== "" && password.length < MIN_PASSWORD_LENGTH
      ? { password: `At least ${MIN_PASSWORD_LENGTH} characters.` }
      : {}),
    ...(confirm !== "" && confirm !== password ? { confirm: "Does not match." } : {}),
  };
}

export type AccountChange =
  | { readonly kind: "disable"; readonly id: string }
  /** The roles the operator would hold afterwards, resolved from their ids. */
  | { readonly kind: "access"; readonly id: string; readonly roles: readonly RoleSummary[] };

/**
 * Why a change would lock the panel out, or `undefined` when it would not.
 *
 * The server's three refusals, mirrored: you may not disable yourself, you may not
 * take the Administrator role from yourself, and nobody may leave the deployment
 * with no active operator holding Administrator — because the only way back from
 * that is a shell on the server and `pnpm admin:create-user`.
 *
 * Only the role counts, not a set of direct permissions that happens to cover the
 * catalog: the server's rule is about the role, and a catalog that grows would
 * quietly leave such an operator short.
 *
 * An invited Administrator does not count. They cannot sign in until they set a
 * password, and if the link is lost the account is no more use than no account.
 *
 * `selfId` is `undefined` for a service token, which has no account here to lose;
 * the last-Administrator rule still applies to it.
 */
export function lockoutReason(
  operators: readonly Operator[],
  selfId: string | undefined,
  change: AccountChange,
): string | undefined {
  const target = operators.find((candidate) => candidate.id === change.id);
  if (target === undefined) return undefined;

  const isAdmin = holdsAdministrator(target.roles);
  const losesAdmin = isAdmin && (change.kind === "disable" || !holdsAdministrator(change.roles));
  const isSelf = selfId !== undefined && change.id === selfId;

  if (isSelf && change.kind === "disable") return "You cannot disable your own account.";
  if (isSelf && losesAdmin) {
    return "You cannot remove the Administrator role from yourself. Ask another administrator.";
  }

  if (!losesAdmin || target.status !== "active") return undefined;

  const remaining = operators.filter(
    (candidate) =>
      candidate.id !== change.id &&
      candidate.status === "active" &&
      holdsAdministrator(candidate.roles),
  );

  return remaining.length === 0
    ? "This is the last active Administrator. Give another operator the role first."
    : undefined;
}
